import { ReactComponent as AppleIcon } from "@assets/icons/mobilePopupIcons/ic_round-apple.svg";
import { ReactComponent as PlayStoreIcon } from "@assets/icons/mobilePopupIcons/playstore.svg";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import DefaultIyugaLogo from "@assets/images/iyuga.jpg";
import { closeMobileViewDialog } from "../../redux/reducers/mobileViewDialog";
import { colors } from "../../utils";
import { ASSETS } from "../../utils/config";
import { fetchImageAsBase64 } from "../../utils/helpers";
import { isInAppBrowser } from "../../utils/browserDetection";
import { useStoreEmailReferralCode } from "../../services/mutations";
import Button from "../Button";
import TextView from "../TextView";
import WheelComponent from "../WheelComponent";
import Clone from "@assets/icons/Clone";
import {
  StyledModal,
  DialogContainer,
  CloseButtonContainer,
  StyledIconButton,
  ContentWrapper,
  WheelContainer,
  IconOverlay,
  EmailInput,
  ErrorText,
  TitleText,
  DescriptionText,
  DescriptionTextWithMargin,
  ContinueBrowserText,
  ContinueBrowserTextWithMargin,
  AppStoreButton,
  SubmitButton,
  CancelButton,
  LoginButton,
  ButtonsContainer,
  EmailFormContainer,
  EmailFormButtonsContainer,
  AppIcon,
  StyledIconComponent,
} from "./styles";

// Utility function to detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const MobileViewDialog = ({ wheelData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen, type, wheel } = useSelector(
    (state) => state.mobileViewDialog
  );
  const [centerImageBase64, setCenterImageBase64] = useState(null);
  const [email, setEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const storeEmailReferralCode = useStoreEmailReferralCode();

  // Transform wheel center image to base64
  useEffect(() => {
    const convertCenterImage = async () => {
      let urlToConvert = wheel?.wheel_center_media?.url;
      if (!urlToConvert) {
        urlToConvert = DefaultIyugaLogo;
      }
      if (urlToConvert && !urlToConvert.startsWith("data:image")) {
        try {
          const base64 = await fetchImageAsBase64(urlToConvert);
          setCenterImageBase64(base64);
        } catch (error) {
          setCenterImageBase64(urlToConvert); // fallback to original if conversion fails
        }
      } else {
        setCenterImageBase64(urlToConvert);
      }
    };
    convertCenterImage();
  }, [wheel?.wheel_center_media?.url]);

  // Map dialog types to icons and colors
  const getIconConfig = (dialogType) => {
    const configs = {
      clone: {
        icon: ContentCopyIcon,
        color: "#0FADAD",
        borderColor: "#0FADAD",
      },
      like: {
        icon: ThumbUpOutlinedIcon,
        color: "#E25297",
        borderColor: "#E25297",
      },
      favorite: {
        icon: FavoriteOutlinedIcon,
        color: "#E25297",
        borderColor: "#E25297",
      },
      unlock: {
        icon: LockOpenOutlinedIcon,
        color: "#ff6b35",
        borderColor: "#ff6b35",
      },
      follow: {
        icon: PersonAddAltOutlinedIcon,
        color: "#9B5AF8",
        borderColor: "#9B5AF8",
      },
      default: {
        icon: LockOpenOutlinedIcon,
        color: "#ff6b35",
        borderColor: "#ff6b35",
      },
    };

    return configs[dialogType] || configs.default;
  };

  // Map dialog types to title text
  const getTitleText = (dialogType) => {
    const titles = {
      clone: "Clone this amazing Wheel on the iYuga mobile app!",
      favorite: "Favorite this amazing Wheel on the iYuga mobile app!",
      unlock: "Unlock this amazing Wheel on the iYuga mobile app!",
      follow: "Follow this User on the iYuga mobile app!",
      default: "Oops! Want to Unlock this amazing Wheel?",
    };

    return titles[dialogType] || titles.default;
  };

  if (!isOpen) return null;

  const handleClose = () => {
    dispatch(closeMobileViewDialog());
    // Reset form state when closing
    setShowEmailForm(false);
    setEmail("");
    setEmailError("");
    setEmailSubmitted(false);
  };

  const handleCancel = () => {
    if (emailSubmitted) {
      setEmailSubmitted(false);
      setEmail("");
      setEmailError("");
    } else if (showEmailForm) {
      setShowEmailForm(false);
      setEmail("");
      setEmailError("");
    } else {
      handleClose();
    }
  };
  console.log("wheel", wheel);

  const getReferralCode = () => {
    // Check sessionStorage first, then localStorage
    let referralData = sessionStorage.getItem("referralData");
    if (!referralData) {
      referralData = localStorage.getItem("referralData");
    }

    if (referralData) {
      try {
        const parsedData = JSON.parse(referralData);
        return parsedData.referral_code || null;
      } catch (error) {
        console.error("Error parsing referralData:", error);
        return null;
      }
    }
    return null;
  };

  const handleGetAppClick = () => {
    setShowEmailForm(true);
  };

  const handleEmailSubmit = async () => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError("This email is invalid");
      return;
    }

    setEmailError("");
    setIsSubmitting(true);

    try {
      const referralCode = getReferralCode();

      if (referralCode) {
        // Call the API to store email with referral code
        await storeEmailReferralCode.mutateAsync({
          email: email.trim(),
          referral_code: referralCode,
        });
      }

      // On success, show download button
      setEmailSubmitted(true);
    } catch (error) {
      console.error("Error submitting email:", error);
      const resp = error?.response;

      // If API returns 400 with the specific email message, treat as success
      if (resp && (resp.status === 400 || resp.status_code === 400)) {
        const respStr = JSON.stringify(resp.data || {});
        if (
          respStr.includes(
            "Email Referral Code with this Email Address already exists"
          )
        ) {
          // The email was already stored previously — show download button
          setEmailSubmitted(true);
        } else if (resp?.data?.message) {
          setEmailError(resp.data.message);
        } else {
          setEmailError("Something went wrong. Please try again.");
        }
      } else if (resp?.status === 409) {
        // Duplicate email error mapped to 409 in other flows
        setEmailError("This email already has a referral code stored.");
      } else if (resp?.data?.message) {
        setEmailError(resp.data.message);
      } else {
        setEmailError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = () => {
    const appStoreUrl =
      "https://apps.apple.com/pk/app/iyuga-wheel/id6746421146";
    const playStoreUrl =
      "https://play.google.com/store/apps/details?id=com.iyuga";

    // Directly redirect to stores
    if (isIOS()) {
      window.location.href = appStoreUrl;
    } else {
      window.location.href = playStoreUrl;
    }
  };

  // old implementation of opening the app
  // const handleOpenAppClick = () => {
  //   const iosDeepLink = "iyuga://"; // Custom URL scheme for iOS fallback
  //   const androidDeepLink =
  //     "intent://#Intent;scheme=iyuga;package=com.iyuga;end";

  //   // Track if app was opened
  //   let appOpened = false;
  //   let timeoutId;

  //   // Handler for when app opens (page loses focus)
  //   const handleBlur = () => {
  //     appOpened = true;
  //     clearTimeout(timeoutId);
  //     window.removeEventListener("blur", handleBlur);
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //     handleClose();
  //   };

  //   // Handler for visibility change (more reliable on mobile)
  //   const handleVisibilityChange = () => {
  //     if (document.hidden) {
  //       appOpened = true;
  //       clearTimeout(timeoutId);
  //       window.removeEventListener("blur", handleBlur);
  //       document.removeEventListener(
  //         "visibilitychange",
  //         handleVisibilityChange
  //       );
  //       handleClose();
  //     }
  //   };

  //   // Set up listeners
  //   window.addEventListener("blur", handleBlur);
  //   document.addEventListener("visibilitychange", handleVisibilityChange);

  //   // Try to open the app via universal link
  //   if (isIOS()) {
  //     window.location.href = iosDeepLink;
  //   } else {
  //     window.location.href = androidDeepLink;
  //   }

  //   // Clean up listeners after timeout
  //   timeoutId = setTimeout(() => {
  //     window.removeEventListener("blur", handleBlur);
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //   }, 2500); // 2.5 second timeout
  // };

  const handleOpenAppClick = () => {
    if (isInAppBrowser()) {
      setShowInAppWarning(true);
      return;
    }

    const wheelPath = wheel?.id ? `wheel/${wheel.id}` : "";
    const appStoreUrl =
      "https://apps.apple.com/pk/app/iyuga-wheel/id6746421146";
    const iosDeepLink = wheelPath ? `iyuga://${wheelPath}` : "iyuga://";
    const androidDeepLink = wheelPath
      ? `intent://${wheelPath}#Intent;scheme=iyuga;package=com.iyuga;end`
      : "intent://#Intent;scheme=iyuga;package=com.iyuga;end";

    if (isIOS()) {
      let timeoutId;
      const handleVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(timeoutId);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.location.href = iosDeepLink;
      timeoutId = setTimeout(() => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.location.href = appStoreUrl;
      }, 2000);
    } else {
      window.location.href = androidDeepLink;
    }
  };

  const handleSignIn = () => {
    navigate("/login");
    handleClose();
  };

  const handleSignUp = () => {
    navigate("/signup");
    handleClose();
  };

  // Prepare wheel data for display
  const wheelSegments = Array.isArray(wheel?.wheel_segments)
    ? wheel.wheel_segments
    : [];

  const wheelUpdatedDataSorted = wheelSegments
    .map((obj) => ({
      ...obj,
      ...obj?.settings,
      selected: false,
    }))
    .sort((a, b) => a.position - b.position);

  const userData = {
    id: uuidv4(),
    url: centerImageBase64 || wheel?.wheel_image || ASSETS.userPlaceholder,
  };

  const iconConfig = getIconConfig(type);
  const IconComponent = iconConfig.icon;
  const titleText = getTitleText(type);

  return (
    <StyledModal open={isOpen} onClose={handleClose}>
      <DialogContainer>
        <CloseButtonContainer>
          <StyledIconButton onClick={handleClose}>
            <CloseIcon />
          </StyledIconButton>
        </CloseButtonContainer>

        <ContentWrapper>
          {/* Wheel Graphic with Padlock */}
          <WheelContainer>
            <WheelComponent
              userData={userData}
              maxSelectedArcs={0}
              arcThickness={-10}
              chartData={wheelUpdatedDataSorted || []}
              margin={25}
              setChartData={() => { }}
              containerDivStyle={{
                width: "120px",
                height: "120px",
              }}
            />
            <IconOverlay borderColor={iconConfig.borderColor}>
              {type === "clone" ? (
                <Clone width={14} height={14} />
              ) : (
                <StyledIconComponent color={iconConfig.color}>
                  <IconComponent />
                </StyledIconComponent>
              )}
            </IconOverlay>
          </WheelContainer>

          {/* Title */}
          <TitleText variant="Header3" color="#123051">
            {showEmailForm
              ? emailSubmitted
                ? "Ready to download!"
                : "Providing your email lets us give credit to the owner of this Wheel"
              : titleText}
          </TitleText>

          {!showEmailForm && (
            <>
              {/* Description */}
              <DescriptionText variant="largeText" color="#123051">
                Want to save this Wheel and build your own Wheels on desktop?
              </DescriptionText>

              {/* App Store Button */}
              <AppStoreButton onClick={handleGetAppClick} fullWidth>
                {isIOS() ? (
                  <AppIcon>
                    <AppleIcon />
                  </AppIcon>
                ) : (
                  <AppIcon>
                    <PlayStoreIcon />
                  </AppIcon>
                )}
                <TextView color={colors.white}>Get the App</TextView>
              </AppStoreButton>


              {!showInAppWarning && (
                <Button onClick={handleOpenAppClick} fullWidth>
                  {isInAppBrowser() ? "Open iYuga App" : "Already have the mobile app? Open here."}
                </Button>
              )}

              {showInAppWarning && (
                <div style={{borderRadius: "8px", padding: "8px", backgroundColor: colors.PrimaryPurple100, border: `2px solid ${colors.PrimaryPurple500}`, paddingTop: "16px"}}>
                  <TextView
                    variant="largeText"
                    style={{
                      textAlign: "center",
                      fontSize: "13px",
                      marginTop: "-8px",
                    }}
                  >
                    Tap the three dots in the corner of your screen and click <b>"Open External Browser"</b>.
                  </TextView>
                </div>
              )}

              {/* Continue on Browser Text */}
              <ContinueBrowserText variant="largeText" color="#123051">
                To continue on this browser:
              </ContinueBrowserText>

              {/* Log in and Sign Up Buttons */}
              <ButtonsContainer>
                <Button variant="outlined" onClick={handleSignIn}>
                  Log in
                </Button>
                <Button onClick={handleSignUp}>Sign Up</Button>
              </ButtonsContainer>
            </>
          )}

          {showEmailForm && (
            <>
              {!emailSubmitted ? (
                <>
                  {/* Description for email form */}
                  <DescriptionTextWithMargin variant="largeText" color="#123051">
                    <b>Don't worry!</b> We won't save your info if you don't create
                    an account within 24 hours.
                  </DescriptionTextWithMargin>

                  <EmailFormContainer>
                    <Box>
                      <EmailInput
                        placeholder="Enter email you will use for the app"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError(""); // Clear error on typing
                        }}
                        type="email"
                        error={!!emailError}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isSubmitting) {
                            handleEmailSubmit();
                          }
                        }}
                      />
                      {emailError && <ErrorText>{emailError}</ErrorText>}
                    </Box>

                    <SubmitButton
                      onClick={handleEmailSubmit}
                      isLoading={isSubmitting}
                      fullWidth
                    >
                      {!isSubmitting ? "Submit" : ""}
                    </SubmitButton>

                    <ContinueBrowserTextWithMargin variant="largeText" color="#123051">
                      To continue on this browser:
                    </ContinueBrowserTextWithMargin>

                    <EmailFormButtonsContainer>
                      <CancelButton variant="outlined" onClick={handleCancel}>
                        Cancel
                      </CancelButton>
                      <LoginButton onClick={handleSignIn}>Log in</LoginButton>
                    </EmailFormButtonsContainer>
                  </EmailFormContainer>
                </>
              ) : (
                <>
                  {/* Description after email submission */}
                  <DescriptionTextWithMargin variant="largeText" color="#123051">
                    Thank you! Click below to download the app.
                  </DescriptionTextWithMargin>

                  <EmailFormContainer>
                    <AppStoreButton onClick={handleDownloadClick} fullWidth>
                      {isIOS() ? (
                        <AppIcon>
                          <AppleIcon />
                        </AppIcon>
                      ) : (
                        <AppIcon>
                          <PlayStoreIcon />
                        </AppIcon>
                      )}
                      <TextView color={colors.white}>Download now</TextView>
                    </AppStoreButton>

                    <ContinueBrowserTextWithMargin variant="largeText" color="#123051">
                      To continue on this browser:
                    </ContinueBrowserTextWithMargin>

                    <EmailFormButtonsContainer>
                      <CancelButton variant="outlined" onClick={handleCancel}>
                        Cancel
                      </CancelButton>
                      <LoginButton onClick={handleSignIn}>Log in</LoginButton>
                    </EmailFormButtonsContainer>
                  </EmailFormContainer>
                </>
              )}
            </>
          )}
        </ContentWrapper>
      </DialogContainer>
    </StyledModal>
  );
};

export default MobileViewDialog;
